import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../../models/Paginate/PagesPaginate";
import { FinancialModel } from "../../../models/Financial/FinancialModel";
import { FinancialsService } from "../../../services/Financial/FinancialsService";

interface ContextProps {
  financials: FinancialModel[];
  params: any;
  setParams: Dispatch<SetStateAction<any>>;
  pages: PagesPaginateModel;
  isLoading: boolean;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
}

export const FinancialsContext = createContext<ContextProps>(
  {} as ContextProps,
);

interface FinancialsProviderProps {
  children: ReactNode;
}

export const FinancialsProvider = ({ children }: FinancialsProviderProps) => {
  const [financials, setFinancials] = useState<FinancialModel[]>([]);
  const [params, setParams] = useState<any>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const service = new FinancialsService();
  const location = useLocation();

  const handleList = useCallback(async () => {
    if (!params || !location.pathname.includes("/articles/categories/list")) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setFinancials(_Response?.items ?? []);
      setPages(_Response?.pagination ?? {});
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setError("Não foi possível carregar as financeiras.");
    }
  }, [params, location.pathname]);

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  return (
    <FinancialsContext.Provider
      value={{
        financials,
        params,
        setParams,
        pages,
        isLoading,
        error,
        setError,
        handleList,
      }}
    >
      {children}
    </FinancialsContext.Provider>
  );
};

export const useFinancials = () => useContext(FinancialsContext);
